import React, { MouseEvent, ReactElement, useState } from 'react'
import { Chip, IconButton, Menu, MenuItem, Stack, Tooltip } from '@mui/material'
import { DisabledState } from '@types'
import { MoreVert } from '@mui/icons-material'

const DisableCell = (props: Props): ReactElement | null => {
  const { anyVariantsDisabled, isVariant, onChange, state, storeTimeZone } =
    props
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  const label = chipLabel(state, isVariant, anyVariantsDisabled)
  const actions = menuActions(props)

  const handleOpenMenu = (event: MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget)
  const handleCloseMenu = () => setAnchorEl(null)

  const handleSelect = (next: DisabledState) => async () => {
    setAnchorEl(null)
    setIsSaving(true)
    await onChange(next)
    setIsSaving(false)
  }

  if (!label && actions.length === 0) {
    return null
  }

  const isDisabled = state !== DisabledState.none || !!anyVariantsDisabled

  const renderChip = () => {
    const chip = (
      <Chip
        color={isDisabled ? 'warning' : 'success'}
        label={label}
        size='small'
        variant='outlined'
      />
    )

    // `until_end_of_day` expires at 11:59 PM in the *store's* TZ
    if (state !== DisabledState.untilEndOfDay || !storeTimeZone) {
      return chip
    }

    return (
      <Tooltip title={`Ends at 11:59 PM in store time (${storeTimeZone})`}>
        {chip}
      </Tooltip>
    )
  }

  return (
    <Stack alignItems='center' direction='row' spacing={0.5}>
      {label && renderChip()}
      {actions.length > 0 && (
        <>
          <IconButton
            aria-label='Change availability'
            disabled={isSaving}
            onClick={handleOpenMenu}
            size='small'
          >
            <MoreVert fontSize='small' />
          </IconButton>
          <Menu anchorEl={anchorEl} onClose={handleCloseMenu} open={!!anchorEl}>
            {actions.map((action) => (
              <MenuItem key={action.state} onClick={handleSelect(action.state)}>
                {action.label}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </Stack>
  )
}

export default DisableCell

type Props = {
  allVariantsDisabled?: boolean
  anyVariantsDisabled?: boolean
  canDisableIndefinitely: boolean
  canDisableProducts: boolean
  isVariant: boolean
  onChange: (state: DisabledState) => Promise<unknown>
  state: DisabledState
  storeTimeZone?: string
}

type Action = {
  label: string
  state: DisabledState
}

const chipLabel = (
  state: DisabledState,
  isVariant: boolean,
  anyVariantsDisabled?: boolean,
): string | null => {
  if (state === DisabledState.untilEndOfDay) {
    return isVariant ? 'Off until 11:59 PM' : 'All variants off until 11:59 PM'
  }
  if (state === DisabledState.indefinitely) {
    return isVariant ? 'Off indefinitely' : 'All variants off indefinitely'
  }
  // It was requested to not display disabling on the variant, so hide
  // as much as possible.
  if (isVariant) {
    return null
  }
  // The product rollup reports 'none' when only *some* variants are off.
  return anyVariantsDisabled ? 'Some variants off' : 'Available'
}

/**
 * Every transition that is valid from the current state, for this user. An
 * empty list means the row is read-only and gets no menu button at all.
 */
const menuActions = ({
  allVariantsDisabled,
  canDisableIndefinitely,
  canDisableProducts,
  isVariant,
  state,
}: Props): Action[] => {
  if (!canDisableProducts) {
    return []
  }

  if (isVariant) {
    // Variants are never disabled from here, but you can re-enable when some other
    // app turned one off, for visibiilty. It was requested for disable feature to
    // apply to product row only.
    if (state === DisabledState.none || allVariantsDisabled) {
      return []
    }
    if (state === DisabledState.indefinitely && !canDisableIndefinitely) {
      return []
    }
    return [{ label: 'Re-enable', state: DisabledState.none }]
  }

  if (state === DisabledState.indefinitely) {
    return canDisableIndefinitely
      ? [{ label: 'Re-enable', state: DisabledState.none }]
      : []
  }

  const actions: Action[] =
    state === DisabledState.untilEndOfDay
      ? [{ label: 'Re-enable', state: DisabledState.none }]
      : [
          {
            label: 'Disable until end of day',
            state: DisabledState.untilEndOfDay,
          },
        ]

  if (canDisableIndefinitely) {
    actions.push({
      label: 'Disable indefinitely',
      state: DisabledState.indefinitely,
    })
  }

  return actions
}
